// Copyright (c) Docugami, Inc. All rights reserved.

import axios from "axios";
import { getExtension, isExtensionSupported } from "components/file_management/file_helpers";
import { Config } from "config";
import { FileManagerDataApi, FileValidationResult } from "networking/api";
import { AsyncActionQueue, IActionItem, IActionStats } from "utils/AsyncActionQueue";
import { Event, IEvent } from "utils/Event";

export interface FileUploadRequest {
    file: File;
    targetPath: string;
    docsetId?: string;
}

export enum BatchUploadStatus {
    None,
    Running,
    Complete
};

export enum FileUploadStatus {
    Started,
    Uploading,
    Succeeded,
    Failed
};

export interface FileUploadEvent {
    status: FileUploadStatus,
    path: string
    uploadedByes: number,
    totalBytes: number
}

export interface BatchUploadEvent {
    status: BatchUploadStatus,
    stats: IActionStats
}

export interface IUploadStats extends IActionStats {
    percentComplete: number
}

interface FileUploadMetadata {
    blobUrl: string
    totalBytes: number
    uploadedBytes: number
    path: string
    docsetId?: string
}

class FileUploader {
    private _uploadQueue: AsyncActionQueue | null = null;
    private _uploadPromise: Promise<void> | null = null;
    private readonly _batchUploadEvent: Event<BatchUploadEvent> = new Event();
    private readonly _fileUploadEvent: Event<FileUploadEvent> = new Event();

    get onBatchUpload(): IEvent<BatchUploadEvent> { return this._batchUploadEvent; }
    get onFileUpload(): IEvent<FileUploadEvent> { return this._fileUploadEvent; }
    get stats(): IUploadStats { return this.createStats(); }

    private get uploadQueue(): AsyncActionQueue {
        if (this._uploadQueue === null) {
            this._uploadQueue = new AsyncActionQueue({ autoRun: false, concurrentActions: 2 });
        }

        return this._uploadQueue;
    }

    async upload(requests: FileUploadRequest[] = []) {
        if (requests.length === 0) {
            return;
        }

        const paths: string[] = [];
        for (const request of requests) {
            const metadata: FileUploadMetadata | null = this.createUploadMetadataForRequest(request);
            if (!metadata) {
                continue;
            }

            if (paths.includes(request.targetPath)) {
                continue;
            }

            paths.push(request.targetPath);

            this.uploadQueue.enqueue(
                () => this.uploadFile(metadata),
                metadata,
                this.onFileUploadStart.bind(this),
                this.onFileUploadComplete.bind(this));
        }

        if (this.uploadQueue.stats.remainingCount === 0) {
            return;
        }

        if (!this._uploadPromise) {
            this._uploadPromise = this.uploadInternal();
        }

        await this._uploadPromise;
    }

    private async uploadInternal(): Promise<void> {
        this.updateBatchStatus(BatchUploadStatus.Running);

        await this.uploadQueue.run();

        // Send the complete event before clearing stats to allow listeners to consume the latest
        this.updateBatchStatus(BatchUploadStatus.Complete);

        await this.uploadQueue.reset();

        this._uploadPromise = null;
        this.updateBatchStatus(BatchUploadStatus.None);
    }

    private createUploadMetadataForRequest(request: FileUploadRequest): FileUploadMetadata | null {
        let metadata: FileUploadMetadata | null = null;

        // Skip hidden and unsupported files
        const extension = getExtension(request.file.name);
        if (!request.file.name.startsWith('.') && isExtensionSupported(extension)) {
            metadata = {
                totalBytes: request.file.size,
                uploadedBytes: 0,
                blobUrl: window.URL.createObjectURL(request.file),
                path: request.targetPath,
                docsetId: request.docsetId
            };
        }

        return metadata;
    }

    private onFileUploadStart(item: IActionItem<void, FileUploadMetadata>) {
        this.raiseFileUploadEvent(FileUploadStatus.Started, item.metadata);
    }

    private onFileUploadComplete(item: IActionItem<void, FileUploadMetadata>, result?: void, error?: any) {
        item.metadata.uploadedBytes = !error ? item.metadata.totalBytes : 0;
        const status = !error ? FileUploadStatus.Succeeded : FileUploadStatus.Failed;
        this.raiseFileUploadEvent(status, item.metadata);
    }

    private async uploadFile(metadata: FileUploadMetadata): Promise<void> {
        this.raiseFileUploadEvent(FileUploadStatus.Uploading, metadata);

        const fileManagerApi = new FileManagerDataApi();

        const fileValidationResult = (await fileManagerApi.fileManagerDataValidateFile(metadata.path)).data;
        if (fileValidationResult !== FileValidationResult.Valid) {
            throw new Error(`Could not upload file. Reason: ${fileValidationResult}`);
        }

        const blobResponse = await fetch(metadata.blobUrl);
        const blob = await blobResponse.blob();

        const uploadProgressCallback = (progressEvent: any) => {
            metadata.uploadedBytes = Math.min(progressEvent.loaded, metadata.totalBytes);
            this.raiseFileUploadEvent(FileUploadStatus.Uploading, metadata);
        }

        const formData = new FormData();
        if (!!metadata.docsetId) {
            formData.append('docset.id', metadata.docsetId);
        }
        formData.append('file', blob, metadata.path);
        await axios.post(`${Config.publicApiBaseUrl}/documents/content`, formData, {
            headers: {
                'Content-Type': "multipart/form-data"
            },
            onUploadProgress: uploadProgressCallback
        });

        URL.revokeObjectURL(metadata.blobUrl);
    }

    private updateBatchStatus(status: BatchUploadStatus) {
        const payload: BatchUploadEvent = {
            status: status,
            stats: { ...this.uploadQueue.stats }
        }

        this._batchUploadEvent.raise(payload);
    }

    private createStats(): IUploadStats {
        const stats: IUploadStats = {
            ...this.uploadQueue.stats,
            percentComplete: 0
        };

        stats.percentComplete = stats.totalCount > 0 ? stats.completedCount / stats.totalCount : 0;

        let numRunning = 0;
        let totalBytes = 0;
        let uploadedBytes = 0;
        this.uploadQueue.getRunningActions<void, FileUploadMetadata>().forEach(item => {
            numRunning++;
            totalBytes += item.metadata.totalBytes;
            uploadedBytes += item.metadata.uploadedBytes;
        });

        if (numRunning > 0) {
            // Calcuate fractional percentage
            const fractional = (numRunning / stats.totalCount) * (uploadedBytes / totalBytes);
            stats.percentComplete += fractional;
        }

        return stats;
    }

    private raiseFileUploadEvent(status: FileUploadStatus, metadata: FileUploadMetadata) {
        this._fileUploadEvent.raise({
            status: status,
            path: metadata.path,
            uploadedByes: metadata.uploadedBytes,
            totalBytes: metadata.totalBytes
        });
    }
}

export const FileUploaderInstance = new FileUploader();
